/**
 * Onboarding · Step 9b — Your deck is live.
 *
 * Shown immediately after the user publishes the deck (or after the
 * publish coachmark). Spotlights the Publish button so the user can
 * see the public URL is there, and explicitly tells them they now have
 * a shareable link. Single "Got it →" CTA advances to the Connections
 * step.
 */

import { useFileRecord } from "@/api/fileRecords";
import { resolveFileRecordViewUrl } from "@cohesive/api-client";
import { memo, useCallback, useEffect, useState } from "react";
import { useCanvasOnboardingUpdate } from "../../../../context/CanvasOnboardingContext";
import { Flourish, StepHeader } from "../shared/StepHeader";
import type { StepProps } from "../shared/types";

export const DeckPublishedStep = memo(function DeckPublishedStep({
  data,
  onAdvance,
}: StepProps) {
  const update = useCanvasOnboardingUpdate();

  const deckNodeId =
    typeof data.deckNodeId === "string" ? data.deckNodeId : null;

  // Read the viewer link from the file record cache so we can show a
  // snippet of it in the card (confirms to the user it's real).
  const { data: deckRecord } = useFileRecord(deckNodeId ?? undefined);
  const publishedUrl = resolveFileRecordViewUrl(deckRecord) ?? null;

  // Spotlight the publish button so the user sees exactly where the link lives.
  // Also dismiss any publish popover the user left open from the previous step:
  // it would cover the spotlight. Dispatched as a window event because
  // `FilePublishButton` renders in the layout-level right pane, outside
  // `CanvasOnboardingProvider`, so React context can't reach it.
  useEffect(() => {
    update({
      lockedViewport: false,
      highlightTargetId: "artifact-publish-button",
    });
    window.dispatchEvent(
      new CustomEvent("cohesive:onboarding:close-publish-popover"),
    );
    return () => {
      update({ highlightTargetId: null });
    };
  }, [update]);

  const [advancing, setAdvancing] = useState(false);

  const handleGotIt = useCallback(() => {
    if (advancing) return;
    setAdvancing(true);
    onAdvance();
  }, [advancing, onAdvance]);

  return (
    <>
      {/* Published-status eyebrow — green dot + explicit "Published"
          label so the user instantly understands the publish action
          succeeded, without having to parse the URL pill below. */}
      <div
        className="_co_onb_settle_1"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "4px 10px",
          borderRadius: 999,
          background: "rgba(34, 197, 94, 0.12)",
          border: "1px solid rgba(34, 197, 94, 0.45)",
          color: "#15803d",
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          alignSelf: "flex-start",
        }}
      >
        <span
          aria-hidden
          style={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: "#22c55e",
            display: "inline-block",
          }}
        />
        Published
      </div>

      <StepHeader
        heading={
          <>
            Your deck is <Flourish>live</Flourish>.
          </>
        }
        lede={
          <>
            Anyone with the link below can view it — no login required. Come
            back to the Publish button whenever you update the deck to push a
            fresh version.
          </>
        }
      />

      {/* Public link block — URL line on top, prominent "Open in new
          tab" action button below so the affordance is unmistakable. */}
      {publishedUrl && (
        <div
          className="_co_onb_settle_3"
          style={{
            marginTop: "var(--co-onb-stack-md)",
            display: "flex",
            flexDirection: "column",
            gap: 10,
            padding: 12,
            background: "var(--co-snap-fill)",
            border: "1px solid var(--co-border)",
            borderRadius: 12,
            maxWidth: "100%",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              minWidth: 0,
            }}
          >
            <LinkIcon />
            <span
              title={publishedUrl}
              style={{
                fontSize: 12,
                fontFamily:
                  "ui-monospace, 'JetBrains Mono', 'SF Mono', Menlo, monospace",
                color: "var(--co-fg)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
                flex: 1,
              }}
            >
              {publishedUrl}
            </span>
          </div>
          <a
            href={publishedUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="nodrag"
            onPointerDown={(e) => e.stopPropagation()}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              padding: "8px 14px",
              borderRadius: 8,
              background: "var(--co-rust)",
              color: "#FBF9F4",
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: "-0.005em",
              textDecoration: "none",
              cursor: "pointer",
              transition: "filter 160ms ease, transform 160ms ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.filter = "brightness(1.05)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.filter = "";
            }}
          >
            <ExternalLinkIcon />
            Open in new tab
          </a>
        </div>
      )}

      {/* CTA */}
      <div
        className="_co_onb_settle_4"
        style={{
          marginTop: "var(--co-onb-stack-md)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "flex-end",
          alignSelf: "flex-end",
          width: "100%",
        }}
      >
        <button
          type="button"
          className={`nodrag _co_onb_finish${advancing ? " _co_onb_finish_pop" : ""}`}
          onClick={handleGotIt}
          onPointerDown={(e) => e.stopPropagation()}
          disabled={advancing}
          aria-label="Continue"
          style={{
            position: "relative",
            minHeight: 38,
            paddingLeft: 18,
            paddingRight: 18,
            fontSize: "var(--co-onb-fs-cta)",
            gap: 7,
          }}
        >
          {advancing ? "Nice" : "Got it"}
          {!advancing && <Arrow />}
        </button>
      </div>
    </>
  );
});

const LinkIcon = memo(function LinkIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      style={{ flexShrink: 0, color: "var(--co-fg-muted)" }}
    >
      <path
        d="M6.5 9.5a2.5 2.5 0 0 0 3.5 0l2-2a2.5 2.5 0 0 0-3.5-3.5l-1 1M9.5 6.5a2.5 2.5 0 0 0-3.5 0l-2 2a2.5 2.5 0 0 0 3.5 3.5l1-1"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
});

const ExternalLinkIcon = memo(function ExternalLinkIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      style={{ flexShrink: 0 }}
    >
      <path
        d="M9.5 2.5h4v4M13.5 2.5L7 9M11 8.5v4a1 1 0 0 1-1 1H3.5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
});

const Arrow = memo(function Arrow() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden
      style={{ transform: "translateY(0.5px)" }}
    >
      <path
        d="M3 7h8m0 0L7.5 3.5M11 7l-3.5 3.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
});
